import { useMemo, useState } from 'react'
import moment from 'moment'
import clsx from 'clsx'
import {
  Button,
  Card,
  Col,
  Paragraph,
  Row,
  Space,
  Title,
} from 'components/common'
import {
  CheckedStatusUpdateModal,
  CheckedStatusUpdateModalProps,
  CheckedUpdateType,
  RenderCancelMessage,
} from 'components/bookings'
import { OrderApproval } from 'lib/graphql/storeadmin'
import { time } from 'lib/str-formatter'

export type CheckedStatusType = 'beforeChecked' | 'checkedIn' | 'checkedOut'

export type BookingDate = {
  checkedInAt?: moment.Moment
  checkedOutAt?: moment.Moment
  date: moment.Moment
  exceededTime?: moment.Moment
}
export interface CheckedStatusUpdateCardProps
  extends Omit<CheckedStatusUpdateModalProps, 'type' | 'onOk' | 'onClose'> {
  loading?: boolean
  disabled?: boolean
  approval: OrderApproval
  checkedStatus: CheckedStatusType
  bookingDate?: BookingDate
  onCheckedUpdate: (
    time: moment.Moment,
    type: CheckedUpdateType
  ) => Promise<void>
}

export const CheckedStatusUpdateCard = ({
  loading,
  disabled,
  approval,
  bookingDate,
  checkedStatus,
  date,
  onCheckedUpdate,
}: CheckedStatusUpdateCardProps) => {
  const [visible, setVisible] = useState<boolean>(false)
  const [updateType, setUpdateType] =
    useState<CheckedUpdateType>('チェックイン')
  const handleCheckedStatusUpdate = (type: CheckedUpdateType) => {
    setUpdateType(type)
    setVisible(true)
  }
  const checkedInAvailable =
    moment(date).format('YYYY/MM/DD') <= moment().format('YYYY/MM/DD')

  const onOk = async (time: moment.Moment) => {
    try {
      await onCheckedUpdate(time, updateType)
    } finally {
      setVisible(false)
    }
  }

  const renderCheckInStatusMessage = () => (
    <div className={clsx('rounded-lg', 'bg-gray-100', 'p-8')}>
      <div className={clsx('flex', 'flex-col', 'flex-center', 'items-center')}>
        <Paragraph>
          現在選択されている予約日は、現在より未来の日付のためチェックインすることができません。
        </Paragraph>
      </div>
    </div>
  )

  const renderButtons = () => (
    <Space direction="vertical" size="middle">
      {checkedStatus === 'beforeChecked' ? (
        <Button
          shape="round"
          style={{ width: '100%' }}
          onClick={() => handleCheckedStatusUpdate('チェックイン')}
          disabled={approval === OrderApproval.Disapproved}
        >
          チェックイン
        </Button>
      ) : (
        <Button
          danger
          shape="round"
          style={{ width: '100%' }}
          onClick={() => handleCheckedStatusUpdate('チェックアウト')}
          disabled={checkedStatus === 'checkedOut'}
        >
          チェックアウト
        </Button>
      )}
      <Button
        type="primary"
        shape="round"
        style={{ width: '100%' }}
        onClick={() => handleCheckedStatusUpdate('クリア')}
        disabled={checkedStatus === 'beforeChecked'}
      >
        ステータスをクリア
      </Button>
    </Space>
  )

  const statusMemo = useMemo(() => {
    if (disabled) {
      return RenderCancelMessage(
        'キャンセルされた予約のため、チェックインステータスを変更することはできません。'
      )
    } else if (!checkedInAvailable) {
      return renderCheckInStatusMessage()
    }
  }, [disabled, checkedInAvailable])

  const checkInOutTimeStatus = useMemo(() => {
    return (
      <>
        <div className={clsx('rounded-lg', 'bg-gray-100', 'p-8', 'mt-10')}>
          <div
            className={clsx('flex', 'flex-col', 'flex-center', 'items-center')}
          >
            <Row>
              <Space>
                <Col style={{ width: '150px' }}> チェックイン時間</Col>
                <Col>{time(bookingDate?.checkedInAt)}</Col>
              </Space>
            </Row>
            {bookingDate?.checkedOutAt && (
              <Row>
                <Space>
                  <Col style={{ width: '150px' }}>チェックアウト時間</Col>
                  <Col>{time(bookingDate?.checkedOutAt)}</Col>
                </Space>
              </Row>
            )}
          </div>
        </div>
      </>
    )
  }, [bookingDate])

  return (
    <>
      <Card shadow loading={loading}>
        <Title level={5}>チェックインステータス</Title>

        {statusMemo || renderButtons()}
        {bookingDate?.checkedInAt && checkInOutTimeStatus}
      </Card>

      <CheckedStatusUpdateModal
        visible={visible}
        onClose={setVisible}
        date={date}
        type={updateType}
        onOk={onOk}
      />
    </>
  )
}
